package com.meistercharts.charts.pv.roofPlanning

import it.neckar.geometry.Coordinates
import it.neckar.geometry.Rectangle
import it.neckar.geometry.Shape
import it.neckar.geometry.Size
import it.neckar.open.unit.si.mm

sealed interface Resizable : Clickable {
  var location: @RoofRelative Coordinates

  /**
   * The size of the [Resizable] (in [mm])
   */
  var size: @mm Size

  val minimumSize: @mm Size

  /**
   * Returns the location and size this element would have after a resize by the given deltas, kept inside the roof's
   * suggested insets and never below [minimumSize] - see [resizedAxis] for which of the two gives way on a roof too
   * small for the element.
   *
   * The deltas are the totals of one gesture and [anchor] is the geometry it started from, so the clamping limits the
   * result and not the path: a pointer led beyond the roof edge and back lands where its distance says, without the
   * overhang.
   *
   * Returns the result instead of applying it: the change itself is a command, so it can be undone.
   */
  fun resizedBy(
    anchor: @RoofRelative @mm Rectangle,
    deltaLocationX: @mm Double = 0.0,
    deltaLocationY: @mm Double = 0.0,
    deltaWidth: @mm Double = 0.0,
    deltaHeight: @mm Double = 0.0,
    model: PvRoofPlanningModel,
  ): @mm Rectangle {
    val horizontal = resizedAxis(
      anchorNear = anchor.location.x,
      anchorFar = anchor.location.x + anchor.size.width,
      deltaNear = deltaLocationX,
      deltaFar = deltaWidth,
      roofNear = model.suggestedRoofInsets.left,
      roofFar = model.roofSize.width - model.suggestedRoofInsets.right,
      minimum = minimumSize.width,
    )

    val vertical = resizedAxis(
      anchorNear = anchor.location.y,
      anchorFar = anchor.location.y + anchor.size.height,
      deltaNear = deltaLocationY,
      deltaFar = deltaHeight,
      roofNear = model.suggestedRoofInsets.bottom,
      roofFar = model.roofSize.height - model.suggestedRoofInsets.top,
      minimum = minimumSize.height,
    )

    return Rectangle(
      Coordinates(horizontal.start, vertical.start),
      Size(horizontal.endInclusive - horizontal.start, vertical.endInclusive - vertical.start),
    )
  }

  /**
   * Returns the bounds for this unusable area
   */
  fun bounds(): @RoofRelative @mm Shape {
    return Rectangle(location, size)
  }

  /**
   * Returns true if this overlaps with a module/area with the given location + size
   */
  fun overlaps(shape: @RoofRelative @mm Shape): Boolean {
    return this.bounds().overlaps(shape)
  }
}

/**
 * Places the near and the far edge of one axis, each kept between [roofNear] and [roofFar] and at least [minimum] apart.
 *
 * [deltaNear] shifts both edges, [deltaFar] the far one on top of that - so a handle on the near edge passes the same
 * value as both, and the far edge stays where the gesture found it. Each edge is then clamped for itself, which is what
 * keeps a clamp on one of them off the other.
 *
 * A roof that cannot hold [minimum] keeps the minimum and lets the far edge stand out; there is no smaller element to
 * fall back to.
 */
private fun resizedAxis(
  anchorNear: @mm Double,
  anchorFar: @mm Double,
  deltaNear: @mm Double,
  deltaFar: @mm Double,
  roofNear: @mm Double,
  roofFar: @mm Double,
  minimum: @mm Double,
): ClosedFloatingPointRange<@mm Double> {
  @mm val near = (anchorNear + deltaNear).coerceIn(roofNear, (roofFar - minimum).coerceAtLeast(roofNear))
  @mm val far = (anchorFar + deltaNear + deltaFar).coerceIn(near + minimum, roofFar.coerceAtLeast(near + minimum))

  return near..far
}
